import { format, formatDistanceToNowStrict, isValid } from 'date-fns'

const number = new Intl.NumberFormat('en-US')
export const formatNumber = (n: number) => number.format(n)

/** 12,400 -> "12.4k"; small numbers stay whole. */
export function formatCompact(n: number): string {
  if (n < 10_000) return number.format(n)
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n)
}

/** Plan prices (and the MRR built from them) are in kronor, as in Stripe; invoices bring their own currency. */
export function formatMoney(cents: number, currency = 'sek'): string {
  try {
    return new Intl.NumberFormat(currency.toLowerCase() === 'sek' ? 'sv-SE' : 'en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100)
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`
  }
}

const parse = (iso: string | null | undefined) => {
  if (!iso) return null
  const d = new Date(iso)
  return isValid(d) ? d : null
}

export function formatDate(iso: string | null | undefined): string {
  const d = parse(iso)
  return d ? format(d, 'd MMM yyyy') : '—'
}

export function formatDateTime(iso: string | null | undefined): string {
  const d = parse(iso)
  return d ? format(d, 'd MMM yyyy, HH:mm') : '—'
}

/** "3 days ago" / "in 5 days"; "never" when there is no date. */
export function formatRelative(iso: string | null | undefined, empty = 'never'): string {
  const d = parse(iso)
  if (!d) return empty
  if (Math.abs(Date.now() - d.getTime()) < 60_000) return 'just now'
  return formatDistanceToNowStrict(d, { addSuffix: true })
}

export const percent = (ratio: number) => `${Math.round(ratio * 100)}%`

export function initials(name: string, email: string): string {
  const source = name.trim() || email
  const parts = source.split(/[\s@.]+/).filter(Boolean)
  return (parts[0]?.[0] ?? '?').toUpperCase() + (parts[1]?.[0] ?? '').toUpperCase()
}
